import { useState, useRef, useEffect, useContext } from "react";
import { useSignalRContext } from "../contexts/signalR/SignalRProvider";
import UIContext from "../contexts/ui/UIContext";
import { REDIRECT_LINK } from "../constants/routes/Redirect";

const useUpdateNotifier = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFunctionRegistered, setIsFunctionRegistered] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const { registerClientFunction, isHubConnected } = useSignalRContext();
  const { addBanner, setIsMajorUpdatePopupVisible } = useContext(UIContext);
  const isBannerShown = useRef(false);
  const hasPopupBeenShown = useRef(false);

  const majorUpdate = "major";
  const minorUpdate = "minor";
  const patchUpdate = "patch";

  const parseVersion = (version) => version.split(".").map(Number);

  // Get the type of update (major, minor, patch, none)
  const getUpdateType = (current, incoming) => {
    const [currMajor, currMinor, currPatch] = parseVersion(current);
    const [incMajor, incMinor, incPatch] = parseVersion(incoming);
    if (incMajor > currMajor) return majorUpdate;
    if (incMinor > currMinor) return minorUpdate;
    if (incPatch > currPatch) return patchUpdate;
    return "none";
  };

  // Handle version notification from SignalR
  const handleVersionNotification = (versionNumber) => {
    const currentVersion = import.meta.env.VITE_VERSION_NUMBER?.trim();
    const receivedVersion = versionNumber?.trim();

    if (!receivedVersion|| hasPopupBeenShown.current || isBannerShown.current || receivedVersion === currentVersion) {
      return;
    }

    const updateType = getUpdateType(currentVersion, receivedVersion);
    console.log(`Update detected: ${updateType} (${currentVersion} → ${receivedVersion})`);

    switch (updateType) {
      case majorUpdate:
        setIsVisible(true); // Show modal for major updates
        hasPopupBeenShown.current = true;
        setIsMajorUpdatePopupVisible(true);
        break;
      case minorUpdate:
      case patchUpdate:
        // Show banner for minor and patch updates
        addBanner(
          <>
            New version available.{REDIRECT_LINK}
          </>
        );
        
        isBannerShown.current = true;
        break;
      default:
        break;
    }
  };

  // Register SignalR function on hub connection
  useEffect(() => {
    if (!isFunctionRegistered && isHubConnected("notificationHub")) {
      console.log("NotificationHub connected. Registering handleVersionNotification...");
      registerClientFunction("notificationHub", "HandleVersionNotification", handleVersionNotification);
      setIsFunctionRegistered(true);
    }
  }, [isHubConnected]);

  // Modal and overlay button handlers
  const handleCloseModal = () => {
    setIsVisible(false);
    setIsOverlayVisible(true);
  };

  // Show modal on overlay button click
  const handleOverlayClick = (event) => {
    event.stopPropagation();
    setIsVisible(true);
    setIsOverlayVisible(false);
  };

  // Clear the cache and reload the page 
  const reload = async () => {
    localStorage.clear();
    window.location.reload();
  };

  // Close modal when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const modalElement = document.querySelector(".update-view-modal");
      if (isVisible && modalElement && !modalElement.contains(event.target)) {
        handleCloseModal();
      }
    };
  
    if (isVisible) {
      document.addEventListener("click", handleClickOutside);
    }
  
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isVisible]);

  return {
    isVisible,
    isOverlayVisible,
    handleCloseModal,
    handleOverlayClick,
    reload,
  };
};

export default useUpdateNotifier;
